import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";
import {
  CustomerSearchItem,
  DocumentModel,
  DocumentLineModel,
  OrderTotals,
} from "../../types";

export interface EnterOrderState {
  selectedCustomer: CustomerSearchItem | null;
  documentData: DocumentModel | null;
  lines: DocumentLineModel[];
  totals: OrderTotals;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  idempotencyKey: string;
}

const initialState: EnterOrderState = {
  selectedCustomer: null,
  documentData: null,
  lines: [],
  totals: {
    subtotal: 0,
    deliveryFee: 0,
    total: 0,
  },
  isLoading: false,
  isSaving: false,
  error: null,
  idempotencyKey: uuidv4(),
};

const enterOrderSlice = createSlice({
  name: "enterOrder",
  initialState,
  reducers: {
    setCustomer: (state, action: PayloadAction<CustomerSearchItem | null>) => {
      state.selectedCustomer = action.payload;
    },
    setDocumentData: (state, action: PayloadAction<DocumentModel>) => {
      state.documentData = action.payload;
    },
    addLine: (state, action: PayloadAction<DocumentLineModel>) => {
      state.lines.push(action.payload);
    },
    updateLine: (
      state,
      action: PayloadAction<{ index: number; line: DocumentLineModel }>
    ) => {
      const { index, line } = action.payload;
      if (index >= 0 && index < state.lines.length) {
        state.lines[index] = line;
      }
    },
    removeLine: (state, action: PayloadAction<number>) => {
      const index = action.payload;
      if (index >= 0 && index < state.lines.length) {
        state.lines.splice(index, 1);
      }
    },
    setLines: (state, action: PayloadAction<DocumentLineModel[]>) => {
      state.lines = action.payload;
    },
    insertLineAtIndex: (
      state,
      action: PayloadAction<{ index: number; line: DocumentLineModel }>
    ) => {
      const { index, line } = action.payload;
      state.lines.splice(index, 0, line);
    },
    clearForm: (state) => {
      state.selectedCustomer = null;
      state.documentData = null;
      state.lines = [];
      state.totals = {
        subtotal: 0,
        deliveryFee: 0,
        total: 0,
      };
      state.error = null;
      state.idempotencyKey = uuidv4(); // Generate new key for next order
    },
    setTotals: (state, action: PayloadAction<OrderTotals>) => {
      state.totals = action.payload;
    },
    calculateTotals: (state) => {
      let subtotal = 0;
      let tax = 0;

      state.lines.forEach((line) => {
        if (line.quantity && line.price) {
          const lineTotal = line.quantity * line.price;
          subtotal += lineTotal;

          const taxPercentage = line.taxPercentage || 0;
          const lineTax = (lineTotal * taxPercentage) / 100;
          tax += lineTax;
        }
      });

      const deliveryFee = state.documentData?.deliveryFeeAmount || 0;
      const deliveryFeeTax = state.documentData?.deliveryFeeTaxPercentage
        ? (deliveryFee * state.documentData.deliveryFeeTaxPercentage) / 100
        : 0;

      tax += deliveryFeeTax;
      const total = subtotal + tax + deliveryFee;

      state.totals = {
        subtotal,
        deliveryFee,
        total,
      };
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setSaving: (state, action: PayloadAction<boolean>) => {
      state.isSaving = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    generateIdempotencyKey: (state) => {
      state.idempotencyKey = uuidv4();
    },
  },
});

export const {
  setCustomer,
  setDocumentData,
  addLine,
  updateLine,
  removeLine,
  setLines,
  insertLineAtIndex,
  clearForm,
  setTotals,
  calculateTotals,
  setLoading,
  setSaving,
  setError,
  generateIdempotencyKey,
} = enterOrderSlice.actions;

export default enterOrderSlice;
